import { ReactElement, useCallback } from 'react';

import Analytics from '@hh.ru/analytics-js';
import { Button } from '@hh.ru/magritte-ui';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import Notices, { MarkAsViewedEvent } from 'Modules/Notices';
import MagritteInfotip from 'src/components/Infotip/MagritteInfotip';
import translation from 'src/components/translation';
import useOnOffState from 'src/hooks/useOnOffState';

const INFOTIP_NAME = 'connectedServicesInfo';

enum ButtonClose {
    Positive = 'positive',
    Negative = 'negative',
}

const TrlKeys = {
    text: 'connected.services.infotip.description.text',
    button: {
        [ButtonClose.Positive]: 'connected.services.infotip.positive.button',
        [ButtonClose.Negative]: 'connected.services.infotip.negative.button',
    },
};

const sendEventElementShown = (element: HTMLDivElement | null) =>
    element && Analytics.sendEventElementShown(element, 'employer', 'show-connected-services-info');

interface Props {
    isFirstResponse: boolean;
    children: ReactElement;
}

const ConnectedServicesInfotip: TranslatedComponent<Props> = ({ isFirstResponse, children, trls }) => {
    const [isVisible, , close] = useOnOffState(isFirstResponse);

    const handleClose = useCallback(
        (type: ButtonClose) => {
            close();
            Notices.markAsViewed(INFOTIP_NAME);
            Analytics.sendEvent('employer', 'click-connected-services-info', type);
        },
        [close]
    );

    return (
        <MagritteInfotip
            name={INFOTIP_NAME}
            placement="bottom-left"
            visible={isVisible}
            markAsViewedEvent={MarkAsViewedEvent.OnHide}
            showBannerOnMobile={false}
            onClose={() => handleClose(ButtonClose.Negative)}
            render={({ activatorRef }) => <span ref={activatorRef}>{children}</span>}
            buttons={(onClose) => (
                <>
                    <Button
                        onClick={() => handleClose(ButtonClose.Positive)}
                        style="contrast"
                        mode="primary"
                        size="small"
                    >
                        {trls[TrlKeys.button.positive]}
                    </Button>
                    <Button style="contrast" mode="secondary" size="small" onClick={onClose}>
                        {trls[TrlKeys.button.negative]}
                    </Button>
                </>
            )}
        >
            <div ref={sendEventElementShown}>{trls[TrlKeys.text]}</div>
        </MagritteInfotip>
    );
};

export default translation(ConnectedServicesInfotip);
